/**
 * ST-05 (AIVA-6805) — the pane owns the one "All actions" palette; these are
 * the two ways other code reaches it without importing TaskPane.
 *
 *  - Open requests: Deckorate's home "All actions" card asks the pane to open it.
 *  - Deckorate commands: a design action chosen in the palette runs inside
 *    DeckorateModule, which only exists while the Deckorate tab is showing.
 *    The pane switches tab and parks the command; the module claims it as it
 *    mounts (or straight away, when it is already mounted).
 */

import type { DeckorateCommand } from "../../../../deckorate/types";

let openListener: (() => void) | null = null;
let openPending = false;

/** Asks the pane to open "All actions". Kept until the pane is listening. */
export function requestAllActions(): void {
  if (openListener) openListener();
  else openPending = true;
}

export function subscribeAllActions(listener: () => void): () => void {
  openListener = listener;
  if (openPending) {
    openPending = false;
    listener();
  }
  return () => {
    if (openListener === listener) openListener = null;
  };
}

let paletteOpen = false;

/** Whether "All actions" is showing — Deckorate's Escape/Backspace ladder leaves the key to it. */
export function isAllActionsOpen(): boolean {
  return paletteOpen;
}

export function setAllActionsOpen(open: boolean): void {
  paletteOpen = open;
}

let commandListener: ((command: DeckorateCommand) => void) | null = null;
let pendingCommand: DeckorateCommand | null = null;

export function requestDeckorateCommand(command: DeckorateCommand): void {
  if (commandListener) commandListener(command);
  else pendingCommand = command;
}

export function subscribeDeckorateCommands(listener: (command: DeckorateCommand) => void): () => void {
  commandListener = listener;
  const parked = pendingCommand;
  pendingCommand = null;
  if (parked) listener(parked);
  return () => {
    if (commandListener === listener) commandListener = null;
  };
}

/** Tests only: forget anything parked between cases. */
export function _resetAllActionsBridgeForTesting(): void {
  openListener = null;
  openPending = false;
  paletteOpen = false;
  commandListener = null;
  pendingCommand = null;
}
